import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Dialog } from "@mui/material";
import { Box, Typography, Divider } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CustomScrollbar from "./CustomScrollbar";
import designTokens from "../styles/designTokens";

interface TermsProps {
  open?: boolean;
  onClose?: () => void;
}

const Terms: React.FC<TermsProps> = ({
  open: openProp,
  onClose: onCloseProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // props가 있으면 props 사용, 없으면 URL 기반
  const open =
    openProp !== undefined ? openProp : location.pathname === "/terms";

  const handleClose = (): void => {
    if (onCloseProp) {
      onCloseProp();
    } else {
      navigate(-1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: designTokens.fill.shape.grey30.dark,
          backdropFilter: `blur(${designTokens.numbers.effects[16]})`,
          WebkitBackdropFilter: `blur(${designTokens.numbers.effects[16]})`,
          boxShadow: `0px 0px ${designTokens.numbers.effects[4]} 0px ${designTokens.effects.blackOpacity10.dark}, 0px 1px 8px 0px rgba(0,0,0,0.12)`,
          color: designTokens.fill.text.grey90.dark,
          borderRadius: {
            xs: designTokens.numbers.cornerRadius[16],
            sm: designTokens.numbers.cornerRadius[24],
            md: designTokens.numbers.cornerRadius[32],
          },
          maxWidth: { xs: "100%", sm: "600px", md: "900px", lg: "1080px" },
          maxHeight: { xs: "100vh", sm: "90vh", md: "720px" },
          width: "100%",
          height: { xs: "100vh", sm: "auto" },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          transition: "none",
          animation: "none",
          transform: "none",
          m: { xs: 0, sm: "auto" },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: {
              xs: designTokens.numbers.cornerRadius[16],
              sm: designTokens.numbers.cornerRadius[24],
              md: designTokens.numbers.cornerRadius[32],
            },
            pointerEvents: "none",
            boxShadow: `
              1px 1px 0px -0.5px inset ${designTokens.fill.shape.grey30.dark},
              -1px -1px 0px -0.5px inset ${designTokens.fill.shape.grey30.dark},
              1px 1px 0.5px -1px inset ${designTokens.fill.shape.common100.dark},
              -1px -1px 0.5px -1px inset ${designTokens.fill.shape.common100.dark},
              0px 0px 3px 0px inset ${designTokens.fill.shape.common100.dark},
              0px 0px 16px 0px inset ${designTokens.fill.shape.grey30.dark}
            `,
          },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: designTokens.fill.background.dim.dark,
        },
      }}
      fullScreen={false}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Top Spacer */}
        <Box
          sx={{
            height: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            flexShrink: 0,
          }}
        />

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: {
              xs: designTokens.numbers.gap[12],
              sm: designTokens.numbers.gap[16],
            },
            px: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Button
              sx={{
                color: designTokens.fill.text.grey90.dark,
                minWidth: "24px",
                width: "24px",
                height: "24px",
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={handleClose}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "15px" }} />
            </Button>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  ...designTokens.typography.styles.heading,
                  color: designTokens.fill.text.grey90.dark,
                }}
              >
                View R Terms of Service
              </Typography>
              <Typography
                sx={{
                  ...designTokens.typography.styles.caption,
                  color: designTokens.fill.text.grey60.dark,
                }}
              >
                Last updated: November 03, 2025
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ borderColor: designTokens.stroke.grey40.dark }} />
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            display: "flex",
            flexShrink: 1,
            px: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            pt: {
              xs: designTokens.numbers.gap[12],
              sm: designTokens.numbers.gap[16],
            },
            pb: 0,
          }}
        >
          <CustomScrollbar
            height="100%"
            width="100%"
            thumbHeight={designTokens.numbers.gap[114] as number}
            borderRadius={designTokens.numbers.cornerRadius[0] as number}
          >
            <Typography
              variant="body1"
              sx={designTokens.typography.styles.label2}
              color={designTokens.fill.text.grey90.dark}
              textAlign="left"
              whiteSpace="pre-line"
              fontSize={{ xs: "13px", sm: "14px", md: "15px" }}
              lineHeight={{ xs: 1.5, sm: 1.6 }}
            >
              <strong>AGREEMENT TO OUR LEGAL TERMS</strong>
              <br />
              <br />
              We are Byond R Inc. ("Company," "we," "us," "our"), a company registered in South Korea at
              <br />
              1201, 21, Hwangseul-ro360bungil, Bungdang-gu, Seongnam-si, Gyeonggi-do 13591.
              <br />
              <br />
              We operate the website http://www.viewr.co (the "Site"), as well as any other related products and services that refer or link to these legal terms (the "Legal Terms") (collectively, the "Services").
              <br />
              <br />
              View R is a platform for organizing, viewing, and sharing 3D models and related content in an interactive workspace. It supports both extended reality (XR) environments (AR/VR) and standard desktop access, enabling users to present, review, or document 3D assets with or without immersive hardware.
              <br />
              <br />
              Typical use cases include design reviews, technical documentation, training materials, and collaborative product development.
              <br />
              <br />
              You can contact us by phone at 1-213-249-1905, email at sales@byond-r.com, or by mail to
              <br />
              1201, 21, Hwangseul-ro360bungil, Bungdang-gu, Seongnam-si, Gyeonggi-do 13591, South Korea.
              <br />
              <br />
              These Legal Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you"), and Byond R Inc., concerning your access to and use of the Services. You agree that by accessing the Services, you have read, understood, and agreed to be bound by all of these Legal Terms.
              <br />
              <br />
              IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.
              <br />
              <br />
              We will provide you with prior notice of any scheduled changes to the Services you are using.
              <br />
              <br />
              The modified Legal Terms will become effective upon posting or notifying you by sales@byond-r.com, as stated in the email message. By continuing to use the Services after the effective date of any changes, you agree to be bound by the modified terms.
              <br />
              <br />
              The Services are intended for users who are at least 18 years old. Persons under the age of 18 are not permitted to use or register for the Services.
              <br />
              <br />
              We recommend that you print a copy of these Legal Terms for your records.
              <br />
              <br />
              <strong>TABLE OF CONTENTS</strong>
              <br />
              1. OUR SERVICES
              <br />
              2. INTELLECTUAL PROPERTY RIGHTS
              <br />
              3. USER REPRESENTATIONS
              <br />
              4. USER REGISTRATION
              <br />
              5. PURCHASES AND PAYMENT
              <br />
              6. SUBSCRIPTIONS
              <br />
              7. PROHIBITED ACTIVITIES
              <br />
              8. USER GENERATED CONTRIBUTIONS
              <br />
              9. CONTRIBUTION LICENSE
              <br />
              10. SOCIAL MEDIA
              <br />
              11. THIRD-PARTY WEBSITES AND CONTENT
              <br />
              12. SERVICES MANAGEMENT
              <br />
              13. PRIVACY POLICY
              <br />
              14. TERM AND TERMINATION
              <br />
              15. MODIFICATIONS AND INTERRUPTIONS
              <br />
              16. GOVERNING LAW
              <br />
              17. DISPUTE RESOLUTION
              <br />
              18. CORRECTIONS
              <br />
              19. DISCLAIMER
              <br />
              20. LIMITATIONS OF LIABILITY
              <br />
              21. INDEMNIFICATION
              <br />
              22. USER DATA
              <br />
              23. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES
              <br />
              24. CALIFORNIA USERS AND RESIDENTS
              <br />
              25. MISCELLANEOUS
              <br />
              26. CONTACT US
              <br />
              <br />
              <strong>1. OUR SERVICES</strong>
              <br />
              <br />
              The information provided when using the Services is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country. Accordingly, those persons who choose to access the Services from other locations do so on their own initiative and are solely responsible for compliance with local laws, if and to the extent local laws are applicable.
              <br />
              <br />
              The Services are not tailored to comply with industry-specific regulations (Health Insurance Portability and Accountability Act (HIPAA), Federal Information Security Management Act (FISMA), etc.), so if your interactions would be subjected to such laws, you may not use the Services. You may not use the Services in a way that would violate the Gramm-Leach-Bliley Act (GLBA).
              <br />
              <br />
              <strong>2. INTELLECTUAL PROPERTY RIGHTS</strong>
              <br />
              <br />
              <strong>Our intellectual property</strong>
              <br />
              We are the owner or the licensee of all intellectual property rights in our Services, including all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics in the Services (collectively, the "Content"), as well as the trademarks, service marks, and logos contained therein (the "Marks").
              <br />
              <br />
              Our Content and Marks are protected by copyright and trademark laws (and various other intellectual property rights and unfair competition laws) and treaties in the United States and around the world.
              <br />
              <br />
              The Content and Marks are provided in or through the Services "AS IS" for your personal, non-commercial use or internal business purpose only.
              <br />
              <br />
              <strong>Your use of our Services</strong>
              <br />
              Subject to your compliance with these Legal Terms, including the "PROHIBITED ACTIVITIES" section below, we grant you a non-exclusive, non-transferable, revocable license to:
              <br />
              <br />
              ● access the Services; and
              <br />
              ● download or print a copy of any portion of the Content to which you have properly gained access,
              <br />
              solely for your personal, non-commercial use or internal business purpose.
              <br />
              <br />
              Except as set out in this section or elsewhere in our Legal Terms, no part of the Services and no Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission. If you wish to make any use of the Services, Content, or Marks other than as set out in this section or elsewhere in our Legal Terms, please address your request to: sales@byond-r.com. If we ever grant you the permission to post, reproduce, or publicly display any part of our Services or Content, you must identify us as the owners or licensors of the Services, Content, or Marks and ensure that any copyright or proprietary notice appears or is visible on posting, reproducing, or displaying our Content.
              <br />
              <br />
              We reserve all rights not expressly granted to you in and to the Services, Content, and Marks. Any breach of these Intellectual Property Rights will constitute a material breach of our Legal Terms and your right to use our Services will terminate immediately.
              <br />
              <br />
              <strong>Your submissions and contributions</strong>
              <br />
              Please review this section and the "PROHIBITED ACTIVITIES" section carefully prior to using our Services to understand the (a) rights you give us and (b) obligations you have when you post or upload any content through the Services.
              <br />
              <br />
              Submissions: By directly sending us any question, comment, suggestion, idea, feedback, or other information about the Services ("Submissions"), you agree to assign to us all intellectual property rights in such Submission. You agree that we shall own this Submission and be entitled to its unrestricted use and dissemination for any lawful purpose, commercial or otherwise, without acknowledgment or compensation to you.
              <br />
              <br />
              Contributions: The Services may invite you to chat, contribute to, or participate in blogs, message boards, online forums, and other functionality during which you may create, submit, post, display, transmit, publish, distribute, or broadcast content and materials to us or through the Services, including but not limited to text, writings, video, audio, photographs, music, graphics, comments, reviews, rating suggestions, personal information, or other material ("Contributions"). Any Submission that is publicly posted shall also be treated as a Contribution.
              <br />
              <br />
              You understand that Contributions may be viewable by other users of the Services and possibly through third-party websites.
              <br />
              <br />
              When you post Contributions, you grant us a license (including use of your name, trademarks, and logos): By posting any Contributions, you grant us an unrestricted, unlimited, irrevocable, perpetual, non-exclusive, transferable, royalty-free, fully-paid, worldwide right, and license to: use, copy, reproduce, distribute, sell, resell, publish, broadcast, retitle, store, publicly perform, publicly display, reformat, translate, excerpt (in whole or in part), and exploit your Contributions (including, without limitation, your image, name, and voice) for any purpose, commercial, advertising, or otherwise, to prepare derivative works of, or incorporate into other works, your Contributions, and to sublicense the licenses granted in this section. Our use and distribution may occur in any media formats and through any media channels. This license includes our use of your name, company name, and franchise name, as applicable, and any of the trademarks, service marks, trade names, logos, and personal and commercial images you provide.
              <br />
              <br />
              You are responsible for what you post or upload: By sending us Submissions and/or posting Contributions through any part of the Services or making Contributions accessible through the Services by linking your account through the Services to any of your social networking accounts, you:
              <br />
              <br />
              ● confirm that you have read and agree with our "PROHIBITED ACTIVITIES" and will not post, send, publish, upload, or transmit through the Services any Submission nor post any Contribution that is illegal, harassing, hateful, harmful, defamatory, obscene, bullying, abusive, discriminatory, threatening to any person or group, sexually explicit, false, inaccurate, deceitful, or misleading;
              <br />
              <br />
              ● to the extent permissible by applicable law, waive any and all moral rights to any such Submission and/or Contribution;
              <br />
              <br />
              ● warrant that any such Submission and/or Contributions are original to you or that you have the necessary rights and licenses to submit such Submissions and/or Contributions and that you have full authority to grant us the above-mentioned rights in relation to your Submissions and/or Contributions; and
              <br />
              <br />
              ● warrant and represent that your Submissions and/or Contributions do not constitute confidential information.
              <br />
              <br />
              You are solely responsible for your Submissions and/or Contributions and you expressly agree to reimburse us for any and all losses that we may suffer because of your breach of (a) this section, (b) any third party's intellectual property rights, or (c) applicable law.
              <br />
              <br />
              We may remove or edit your Content: Although we have no obligation to monitor any Contributions, we shall have the right to remove or edit any Contributions at any time without notice if in our reasonable opinion we consider such Contributions harmful or in breach of these Legal Terms. If we remove or edit any such Contributions, we may also suspend or disable your account and report you to the authorities.
              <br />
              <br />
              <strong>3. USER REPRESENTATIONS</strong>
              <br />
              <br />
              By using the Services, you represent and warrant that: (1) all registration information you submit will be true, accurate, current, and complete; (2) you will maintain the accuracy of such information and promptly update such registration information as necessary; (3) you have the legal capacity and you agree to comply with these Legal Terms; (4) you are not a minor in the jurisdiction in which you reside; (5) you will not access the Services through automated or non-human means, whether through a bot, script or otherwise; (6) you will not use the Services for any illegal or unauthorized purpose; and (7) your use of the Services will not violate any applicable law or regulation.
              <br />
              <br />
              If you provide any information that is untrue, inaccurate, not current, or incomplete, we have the right to suspend or terminate your account and refuse any and all current or future use of the Services (or any portion thereof).
              <br />
              <br />
              <strong>4. USER REGISTRATION</strong>
              <br />
              <br />
              You may be required to register to use the Services. You agree to keep your password confidential and will be responsible for all use of your account and password. We reserve the right to remove, reclaim, or change a username you select if we determine, in our sole discretion, that such username is inappropriate, obscene, or otherwise objectionable.
              <br />
              <br />
              <strong>5. PURCHASES AND PAYMENT</strong>
              <br />
              <br />
              We accept the following forms of payment:
              <br />
              - Visa
              <br />
              - Mastercard
              <br />
              <br />
              You agree to provide current, complete, and accurate purchase and account information for all purchases made via the Services. You further agree to promptly update account and payment information, including email address, payment method, and payment card expiration date, so that we can complete your transactions and contact you as needed. Sales tax will be added to the price of purchases as deemed required by us. We may change prices at any time. All payments shall be in US dollars.
              <br />
              <br />
              You agree to pay all charges at the prices then in effect for your purchases and any applicable shipping fees, and you authorize us to charge your chosen payment provider for any such amounts upon placing your order. We reserve the right to correct any errors or mistakes in pricing, even if we have already requested or received payment.
              <br />
              <br />
              We reserve the right to refuse any order placed through the Services. We may, in our sole discretion, limit or cancel quantities purchased per person, per household, or per order. These restrictions may include orders placed by or under the same customer account, the same payment method, and/or orders that use the same billing or shipping address. We reserve the right to limit or prohibit orders that, in our sole judgment, appear to be placed by dealers, resellers, or distributors.
              <br />
              <br />
              <strong>6. SUBSCRIPTIONS</strong>
              <br />
              <br />
              <strong>Billing and Renewal</strong>
              <br />
              Your subscription will continue and automatically renew unless canceled. You consent to our charging your payment method on a recurring basis without requiring your prior approval for each recurring charge, until such time as you cancel the applicable order. The length of your billing cycle will depend on the type of subscription plan you choose when you subscribed to the Services.
              <br />
              <br />
              <strong>Free trial</strong>
              <br />
              We offer a 30-day free trial to new users who register with the Services. The account will not be charged and the subscription will be suspended until upgraded to a paid version at the end of the free trial.
              <br />
              <br />
              <strong>Cancellation</strong>
              <br />
              You can cancel your subscription at any time by logging into your account. Your cancellation will take effect at the end of the current paid term. If you have any questions or are unsatisfied with our Services, please email us at sales@byond-r.com.
              <br />
              <br />
              <strong>Fee Changes</strong>
              <br />
              We may, from time to time, make changes to the subscription fee and will communicate any price changes to you in accordance with applicable law.
              <br />
              <br />
              <strong>7. PROHIBITED ACTIVITIES</strong>
              <br />
              <br />
              You may not access or use the Services for any purpose other than that for which we make the Services available. The Services may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.
              <br />
              <br />
              As a user of the Services, you agree not to:
              <br />
              <br />
              ● Systematically retrieve data or other content from the Services to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us.
              <br />
              <br />
              ● Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords.
              <br />
              <br />
              ● Circumvent, disable, or otherwise interfere with security-related features of the Services, including features that prevent or restrict the use or copying of any Content or enforce limitations on the use of the Services and/or the Content contained therein.
              <br />
              <br />
              ● Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services.
              <br />
              <br />
              ● Use any information obtained from the Services in order to harass, abuse, or harm another person.
              <br />
              <br />
              ● Make improper use of our support services or submit false reports of abuse or misconduct.
              <br />
              <br />
              ● Use the Services in a manner inconsistent with any applicable laws or regulations.
              <br />
              <br />
              ● Engage in unauthorized framing of or linking to the Services.
              <br />
              <br />
              ● Upload or transmit (or attempt to upload or to transmit) viruses, Trojan horses, or other material, including excessive use of capital letters and spamming (continuous posting of repetitive text), that interferes with any party's uninterrupted use and enjoyment of the Services or modifies, impairs, disrupts, alters, or interferes with the use, features, functions, operation, or maintenance of the Services.
              <br />
              <br />
              ● Engage in any automated use of the system, such as using scripts to send comments or messages, or using any data mining, robots, or similar data gathering and extraction tools.
              <br />
              <br />
              ● Delete the copyright or other proprietary rights notice from any Content.
              <br />
              <br />
              ● Attempt to impersonate another user or person or use the username of another user.
              <br />
              <br />
              ● Upload or transmit (or attempt to upload or to transmit) any material that acts as a passive or active information collection or transmission mechanism, including without limitation, clear graphics interchange formats ("gifs"), 1×1 pixels, web bugs, cookies, or other similar devices (sometimes referred to as "spyware" or "passive collection mechanisms" or "pcms").
              <br />
              <br />
              ● Interfere with, disrupt, or create an undue burden on the Services or the networks or services connected to the Services.
              <br />
              <br />
              ● Harass, annoy, intimidate, or threaten any of our employees or agents engaged in providing any portion of the Services to you.
              <br />
              <br />
              ● Attempt to bypass any measures of the Services designed to prevent or restrict access to the Services, or any portion of the Services.
              <br />
              <br />
              ● Copy or adapt the Services' software, including but not limited to Flash, PHP, HTML, JavaScript, or other code.
              <br />
              <br />
              ● Except as permitted by applicable law, decipher, decompile, disassemble, or reverse engineer any of the software comprising or in any way making up a part of the Services.
              <br />
              <br />
              ● Except as may be the result of standard search engine or Internet browser usage, use, launch, develop, or distribute any automated system, including without limitation, any spider, robot, cheat utility, scraper, or offline reader that accesses the Services, or use or launch any unauthorized script or other software.
              <br />
              <br />
              ● Use a buying agent or purchasing agent to make purchases on the Services.
              <br />
              <br />
              ● Make any unauthorized use of the Services, including collecting usernames and/or email addresses of users by electronic or other means for the purpose of sending unsolicited email, or creating user accounts by automated means or under false pretenses.
              <br />
              <br />
              ● Use the Services as part of any effort to compete with us or otherwise use the Services and/or the Content for any revenue-generating endeavor or commercial enterprise.
              <br />
              <br />
              <strong>8. USER GENERATED CONTRIBUTIONS</strong>
              <br />
              <br />
              The Services may invite you to chat, contribute to, or participate in blogs, message boards, online forums, and other functionality, and may provide you with the opportunity to create, submit, post, display, transmit, perform, publish, distribute, or broadcast content and materials to us or on the Services, including but not limited to text, writings, video, audio, photographs, graphics, comments, suggestions, or personal information or other material (collectively, "Contributions"). Contributions may be viewable by other users of the Services and through third-party websites. As such, any Contributions you transmit may be treated as non-confidential and non-proprietary.
              <br />
              <br />
              When you create or make available any Contributions, you thereby represent and warrant that:
              <br />
              <br />
              ● The creation, distribution, transmission, public display, or performance, and the accessing, downloading, or copying of your Contributions do not and will not infringe the proprietary rights, including but not limited to the copyright, patent, trademark, trade secret, or moral rights of any third party.
              <br />
              <br />
              ● You are the creator and owner of or have the necessary licenses, rights, consents, releases, and permissions to use and to authorize us, the Services, and other users of the Services to use your Contributions in any manner contemplated by the Services and these Legal Terms.
              <br />
              <br />
              ● You have the written consent, release, and/or permission of each and every identifiable individual person in your Contributions to use the name or likeness of each and every such identifiable individual person to enable inclusion and use of your Contributions in any manner contemplated by the Services and these Legal Terms.
              <br />
              <br />
              ● Your Contributions are not false, inaccurate, or misleading.
              <br />
              <br />
              ● Your Contributions are not unsolicited or unauthorized advertising, promotional materials, pyramid schemes, chain letters, spam, mass mailings, or other forms of solicitation.
              <br />
              <br />
              ● Your Contributions are not obscene, lewd, lascivious, filthy, violent, harassing, libelous, slanderous, or otherwise objectionable (as determined by us).
              <br />
              <br />
              ● Your Contributions do not ridicule, mock, disparage, intimidate, or abuse anyone.
              <br />
              <br />
              ● Your Contributions are not used to harass or threaten (in the legal sense of those terms) any other person and to promote violence against a specific person or class of people.
              <br />
              <br />
              ● Your Contributions do not violate any applicable law, regulation, or rule.
              <br />
              <br />
              ● Your Contributions do not violate the privacy or publicity rights of any third party.
              <br />
              <br />
              ● Your Contributions do not violate any applicable law concerning child pornography, or otherwise intended to protect the health or well-being of minors.
              <br />
              <br />
              ● Your Contributions do not include any offensive comments that are connected to race, national origin, gender, sexual preference, or physical handicap.
              <br />
              <br />
              ● Your Contributions do not otherwise violate, or link to material that violates, any provision of these Legal Terms, or any applicable law or regulation.
              <br />
              <br />
              Any use of the Services in violation of the foregoing violates these Legal Terms and may result in, among other things, termination or suspension of your rights to use the Services.
              <br />
              <br />
              <strong>9. CONTRIBUTION LICENSE</strong>
              <br />
              <br />
              By posting your Contributions to any part of the Services or making Contributions accessible to the Services by linking your account from the Services to any of your social networking accounts, you automatically grant, and you represent and warrant that you have the right to grant, to us an unrestricted, unlimited, irrevocable, perpetual, non-exclusive, transferable, royalty-free, fully-paid, worldwide right, and license to host, use, copy, reproduce, disclose, sell, resell, publish, broadcast, retitle, archive, store, cache, publicly perform, publicly display, reformat, translate, transmit, excerpt (in whole or in part), and distribute such Contributions (including, without limitation, your image and voice) for any purpose, commercial, advertising, or otherwise, and to prepare derivative works of, or incorporate into other works, such Contributions, and grant and authorize sublicenses of the foregoing. The use and distribution may occur in any media formats and through any media channels.
              <br />
              <br />
              This license will apply to any form, media, or technology now known or hereafter developed, and includes our use of your name, company name, and franchise name, as applicable, and any of the trademarks, service marks, trade names, logos, and personal and commercial images you provide. You waive all moral rights in your Contributions, and you warrant that moral rights have not otherwise been asserted in your Contributions.
              <br />
              <br />
              We do not assert any ownership over your Contributions. You retain full ownership of all of your Contributions and any intellectual property rights or other proprietary rights associated with your Contributions. We are not liable for any statements or representations in your Contributions provided by you in any area on the Services. You are solely responsible for your Contributions to the Services and you expressly agree to exonerate us from any and all responsibility and to refrain from any legal action against us regarding your Contributions.
              <br />
              <br />
              We have the right, in our sole and absolute discretion, (1) to edit, redact, or otherwise change any Contributions; (2) to re-categorize any Contributions to place them in more appropriate locations on the Services; and (3) to pre-screen or delete any Contributions at any time and for any reason, without notice. We have no obligation to monitor your Contributions.
              <br />
              <br />
              <strong>10. SOCIAL MEDIA</strong>
              <br />
              <br />
              As part of the functionality of the Services, you may link your account with online accounts you have with third-party service providers (each such account, a "Third-Party Account") by either: (1) providing your Third-Party Account login information through the Services; or (2) allowing us to access your Third-Party Account, as is permitted under the applicable terms and conditions that govern your use of each Third-Party Account. You represent and warrant that you are entitled to disclose your Third-Party Account login information to us and/or grant us access to your Third-Party Account, without breach by you of any of the terms and conditions that govern your use of the applicable Third-Party Account, and without obligating us to pay any fees or making us subject to any usage limitations imposed by the third-party service provider of the Third-Party Account.
              <br />
              <br />
              By granting us access to any Third-Party Accounts, you understand that (1) we may access, make available, and store (if applicable) any content that you have provided to and stored in your Third-Party Account (the "Social Network Content") so that it is available on and through the Services via your account, including without limitation any friend lists and (2) we may submit to and receive from your Third-Party Account additional information to the extent you are notified when you link your account with the Third-Party Account.
              <br />
              <br />
              PLEASE NOTE THAT YOUR RELATIONSHIP WITH THE THIRD-PARTY SERVICE PROVIDERS ASSOCIATED WITH YOUR THIRD-PARTY ACCOUNTS IS GOVERNED SOLELY BY YOUR AGREEMENT(S) WITH SUCH THIRD-PARTY SERVICE PROVIDERS. We make no effort to review any Social Network Content for any purpose, including but not limited to, for accuracy, legality, or non-infringement, and we are not responsible for any Social Network Content.
              <br />
              <br />
              <strong>11. THIRD-PARTY WEBSITES AND CONTENT</strong>
              <br />
              <br />
              The Services may contain (or you may be sent via the Site) links to other websites ("Third-Party Websites") as well as articles, photographs, text, graphics, pictures, designs, music, sound, video, information, applications, software, and other content or items belonging to or originating from third parties ("Third-Party Content"). Such Third-Party Websites and Third-Party Content are not investigated, monitored, or checked for accuracy, appropriateness, or completeness by us, and we are not responsible for any Third-Party Websites accessed through the Services or any Third-Party Content posted on, available through, or installed from the Services, including the content, accuracy, offensiveness, opinions, reliability, privacy practices, or other policies of or contained in the Third-Party Websites or the Third-Party Content.
              <br />
              <br />
              Inclusion of, linking to, or permitting the use or installation of any Third-Party Websites or any Third-Party Content does not imply approval or endorsement thereof by us. If you decide to leave the Services and access the Third-Party Websites or to use or install any Third-Party Content, you do so at your own risk, and you should be aware these Legal Terms no longer govern. You should review the applicable terms and policies, including privacy and data gathering practices, of any website to which you navigate from the Services or relating to any applications you use or install from the Services.
              <br />
              <br />
              Any purchases you make through Third-Party Websites will be through other websites and from other companies, and we take no responsibility whatsoever in relation to such purchases which are exclusively between you and the applicable third party. You agree and acknowledge that we do not endorse the products or services offered on Third-Party Websites and you shall hold us blameless from any harm caused by your purchase of such products or services. Additionally, you shall hold us blameless from any losses sustained by you or harm caused to you relating to or resulting in any way from any Third-Party Content or any contact with Third-Party Websites.
              <br />
              <br />
              <strong>12. SERVICES MANAGEMENT</strong>
              <br />
              <br />
              We reserve the right, but not the obligation, to:
              <br />
              <br />
              (1) monitor the Services for violations of these Legal Terms;
              <br />
              <br />
              (2) take appropriate legal action against anyone who, in our sole discretion, violates the law or these Legal Terms, including without limitation, reporting such user to law enforcement authorities;
              <br />
              <br />
              (3) in our sole discretion and without limitation, refuse, restrict access to, limit the availability of, or disable (to the extent technologically feasible) any of your Contributions or any portion thereof;
              <br />
              <br />
              (4) in our sole discretion and without limitation, notice, or liability, to remove from the Services or otherwise disable all files and content that are excessive in size or are in any way burdensome to our systems; and
              <br />
              <br />
              (5) otherwise manage the Services in a manner designed to protect our rights and property and to facilitate the proper functioning of the Services.
              <br />
              <br />
              <strong>13. PRIVACY POLICY</strong>
              <br />
              <br />
              We care about data privacy and security. Please review our Privacy Policy: http://www.viewr.co. By using the Services, you agree to be bound by our Privacy Policy, which is incorporated into these Legal Terms. Please be advised the Services are hosted in the United States. If you access the Services from any other region of the world with laws or other requirements governing personal data collection, use, or disclosure that differ from applicable laws in the United States, then through your continued use of the Services, you are transferring your data to the United States, and you expressly consent to have your data transferred to and processed in the United States.
              <br />
              <br />
              <strong>14. TERM AND TERMINATION</strong>
              <br />
              <br />
              These Legal Terms shall remain in full force and effect while you use the Services. WITHOUT LIMITING ANY OTHER PROVISION OF THESE LEGAL TERMS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE SERVICES (INCLUDING BLOCKING CERTAIN IP ADDRESSES), TO ANY PERSON FOR ANY REASON OR FOR NO REASON, INCLUDING WITHOUT LIMITATION FOR BREACH OF ANY REPRESENTATION, WARRANTY, OR COVENANT CONTAINED IN THESE LEGAL TERMS OR OF ANY APPLICABLE LAW OR REGULATION. WE MAY TERMINATE YOUR USE OR PARTICIPATION IN THE SERVICES OR DELETE YOUR ACCOUNT AND ANY CONTENT OR INFORMATION THAT YOU POSTED AT ANY TIME, WITHOUT WARNING, IN OUR SOLE DISCRETION.
              <br />
              <br />
              If we terminate or suspend your account for any reason, you are prohibited from registering and creating a new account under your name, a fake or borrowed name, or the name of any third party, even if you may be acting on behalf of the third party. In addition to terminating or suspending your account, we reserve the right to take appropriate legal action, including without limitation pursuing civil, criminal, and injunctive redress.
              <br />
              <br />
              <strong>15. MODIFICATIONS AND INTERRUPTIONS</strong>
              <br />
              <br />
              We reserve the right to change, modify, or remove the contents of the Services at any time or for any reason at our sole discretion without notice. However, we have no obligation to update any information on our Services. We will not be liable to you or any third party for any modification, price change, suspension, or discontinuance of the Services.
              <br />
              <br />
              We cannot guarantee the Services will be available at all times. We may experience hardware, software, or other problems or need to perform maintenance related to the Services, resulting in interruptions, delays, or errors. We reserve the right to change, revise, update, suspend, discontinue, or otherwise modify the Services at any time or for any reason without notice to you. You agree that we have no liability whatsoever for any loss, damage, or inconvenience caused by your inability to access or use the Services during any downtime or discontinuance of the Services. Nothing in these Legal Terms will be construed to obligate us to maintain and support the Services or to supply any corrections, updates, or releases in connection therewith.
              <br />
              <br />
              <strong>16. GOVERNING LAW / 17. DISPUTE RESOLUTION</strong>
              <br />
              <br />
              These Legal Terms shall be governed by and defined following the laws of South Korea. Byond R Inc. and yourself irrevocably consent that the courts of South Korea shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these Legal Terms.
              <br />
              <br />
              You agree to irrevocably submit all disputes related to these Legal Terms or the legal relationship established by these Legal Terms to the jurisdiction of the South Korea courts. Byond R Inc. shall also maintain the right to bring proceedings as to the substance of the matter in the courts of the country where you reside or, if these Legal Terms are entered into in the course of your trade or profession, the state of your principal place of business.
              <br />
              <br />
              <strong>18. CORRECTIONS</strong>
              <br />
              <br />
              There may be information on the Services that contains typographical errors, inaccuracies, or omissions, including descriptions, pricing, availability, and various other information. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update the information on the Services at any time, without prior notice.
              <br />
              <br />
              <strong>19. DISCLAIMER</strong>
              <br />
              <br />
              THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES AND YOUR USE THEREOF, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTIES OR REPRESENTATIONS ABOUT THE ACCURACY OR COMPLETENESS OF THE SERVICES' CONTENT OR THE CONTENT OF ANY WEBSITES OR MOBILE APPLICATIONS LINKED TO THE SERVICES AND WE WILL ASSUME NO LIABILITY OR RESPONSIBILITY FOR ANY (1) ERRORS, MISTAKES, OR INACCURACIES OF CONTENT AND MATERIALS, (2) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER, RESULTING FROM YOUR ACCESS TO AND USE OF THE SERVICES, (3) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND ALL PERSONAL INFORMATION AND/OR FINANCIAL INFORMATION STORED THEREIN, (4) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SERVICES, (5) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE WHICH MAY BE TRANSMITTED TO OR THROUGH THE SERVICES BY ANY THIRD PARTY, AND/OR (6) ANY ERRORS OR OMISSIONS IN ANY CONTENT AND MATERIALS OR FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF THE USE OF ANY CONTENT POSTED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE VIA THE SERVICES. WE DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH THE SERVICES, ANY HYPERLINKED WEBSITE, OR ANY WEBSITE OR MOBILE APPLICATION FEATURED IN ANY BANNER OR OTHER ADVERTISING, AND WE WILL NOT BE A PARTY TO OR IN ANY WAY BE RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND ANY THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES. AS WITH THE PURCHASE OF A PRODUCT OR SERVICE THROUGH ANY MEDIUM OR IN ANY ENVIRONMENT, YOU SHOULD USE YOUR BEST JUDGMENT AND EXERCISE CAUTION WHERE APPROPRIATE.
              <br />
              <br />
              <strong>20. LIMITATIONS OF LIABILITY</strong>
              <br />
              <br />
              IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF THE SERVICES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN, OUR LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER AND REGARDLESS OF THE FORM OF THE ACTION, WILL AT ALL TIMES BE LIMITED TO THE LESSER OF THE AMOUNT PAID, IF ANY, BY YOU TO US DURING THE SIX (6) MONTH PERIOD PRIOR TO ANY CAUSE OF ACTION ARISING OR $100.00 USD.
              <br />
              <br />
              CERTAIN US STATE LAWS AND INTERNATIONAL LAWS DO NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES OR THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO YOU, SOME OR ALL OF THE ABOVE DISCLAIMERS OR LIMITATIONS MAY NOT APPLY TO YOU, AND YOU MAY HAVE ADDITIONAL RIGHTS.
              <br />
              <br />
              <strong>21. INDEMNIFICATION</strong>
              <br />
              <br />
              You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and all of our respective officers, agents, partners, and employees, from and against any loss, damage, liability, claim, or demand, including reasonable attorneys' fees and expenses, made by any third party due to or arising out of:
              <br />
              <br />
              (1) your Contributions;
              <br />
              (2) use of the Services;
              <br />
              (3) breach of these Legal Terms;
              <br />
              (4) any breach of your representations and warranties set forth in these Legal Terms;
              <br />
              (5) your violation of the rights of a third party, including but not limited to intellectual property rights; or
              <br />
              (6) any overt harmful act toward any other user of the Services with whom you connected via the Services.
              <br />
              <br />
              Notwithstanding the foregoing, we reserve the right, at your expense, to assume the exclusive defense and control of any matter for which you are required to indemnify us, and you agree to cooperate, at your expense, with our defense of such claims. We will use reasonable efforts to notify you of any such claim, action, or proceeding which is subject to this indemnification upon becoming aware of it.
              <br />
              <br />
              <strong>22. USER DATA</strong>
              <br />
              <br />
              We will maintain certain data that you transmit to the Services for the purpose of managing the performance of the Services, as well as data relating to your use of the Services. Although we perform regular routine backups of data, you are solely responsible for all data that you transmit or that relates to any activity you have undertaken using the Services. You agree that we shall have no liability to you for any loss or corruption of any such data.
              <br />
              <br />
              <strong>23. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES</strong>
              <br />
              <br />
              Visiting the Services, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications, and you agree that all agreements, notices, disclosures, and other communications we provide to you electronically, via email and on the Services, satisfy any legal requirement that such communication be in writing.
              <br />
              <br />
              YOU HEREBY AGREE TO THE USE OF ELECTRONIC SIGNATURES, CONTRACTS, ORDERS, AND OTHER RECORDS, AND TO ELECTRONIC DELIVERY OF NOTICES, POLICIES, AND RECORDS OF TRANSACTIONS INITIATED OR COMPLETED BY US OR VIA THE SERVICES. You hereby waive any rights or requirements under any statutes, regulations, rules, ordinances, or other laws in any jurisdiction which require an original signature or delivery or retention of non-electronic records, or to payments or the granting of credits by any means other than electronic means.
              <br />
              <br />
              <strong>24. CALIFORNIA USERS AND RESIDENTS</strong>
              <br />
              <br />
              If any complaint with us is not satisfactorily resolved, you can contact the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs in writing at 1625 North Market Blvd., Suite N 112, Sacramento, California 95834 or by telephone at (800) 952-5210 or (916) 445-1254.
              <br />
              <br />
              <strong>25. MISCELLANEOUS</strong>
              <br />
              <br />
              These Legal Terms and any policies or operating rules posted by us on the Services or in respect to the Services constitute the entire agreement and understanding between you and us. Our failure to exercise or enforce any right or provision of these Legal Terms shall not operate as a waiver of such right or provision. These Legal Terms operate to the fullest extent permissible by law. We may assign any or all of our rights and obligations to others at any time. We shall not be responsible or liable for any loss, damage, delay, or failure to act caused by any cause beyond our reasonable control. If any provision or part of a provision of these Legal Terms is determined to be unlawful, void, or unenforceable, that provision or part of the provision is deemed severable from these Legal Terms and does not affect the validity and enforceability of any remaining provisions. There is no joint venture, partnership, employment or agency relationship created between you and us as a result of these Legal Terms or use of the Services. You agree that these Legal Terms will not be construed against us by virtue of having drafted them. You hereby waive any and all defenses you may have based on the electronic form of these Legal Terms and the lack of signing by the parties hereto to execute these Legal Terms.
              <br />
              <br />
              <strong>26. CONTACT US</strong>
              <br />
              <br />
              In order to resolve a complaint regarding the Services or to receive further information regarding use of the Services, please contact us at:
              <br />
              <br />
              Byond R Inc.
              <br />
              1201, 21, Hwangseul-ro360bungil, Bungdang-gu
              <br />
              Seongnam-si, Gyeonggi-do 13591
              <br />
              South Korea
              <br />
              Phone: 1-213-249-1905
              <br />
              sales@byond-r.com
            </Typography>
          </CustomScrollbar>
        </Box>

        {/* Bottom Spacer */}
        <Box
          sx={{
            height: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            flexShrink: 0,
          }}
        />
      </Box>
    </Dialog>
  );
};

export default Terms;
